import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/component/ds/utils';

/* "Button - Vega" in Figma. States map to pseudo-classes and aria-invalid. */
const buttonVariants = cva(
  [
    'ds-inline-flex ds-items-center ds-justify-center ds-whitespace-nowrap',
    'ds-rounded-full ds-font-medium',
    'ds-transition-colors',
    'focus-visible:ds-outline-none focus-visible:ds-shadow-chemelex-focus',
    'disabled:ds-pointer-events-none disabled:ds-opacity-50',
    'aria-[invalid=true]:ds-shadow-chemelex-focus-error',
    '[&_svg]:ds-pointer-events-none [&_svg]:ds-shrink-0',
    "[&_svg:not([class*='size-']):not(.MuiSvgIcon-root)]:ds-size-4"
  ],
  {
    variants: {
      variant: {
        primary: 'ds-bg-primary ds-text-primary-foreground hover:ds-opacity-90 active:ds-opacity-90',
        secondary: 'ds-bg-secondary ds-text-secondary-foreground hover:ds-opacity-80 active:ds-opacity-80',
        destructive: 'ds-bg-destructive ds-text-destructive-foreground hover:ds-opacity-90 active:ds-opacity-100',
        outline: 'ds-ring-1 ds-ring-inset ds-ring-border ds-bg-background ds-text-foreground hover:ds-bg-muted active:ds-bg-secondary',
        ghost: 'ds-bg-transparent ds-text-foreground hover:ds-bg-muted active:ds-bg-secondary',
        link: 'ds-bg-transparent ds-text-foreground ds-underline-offset-4 hover:ds-underline'
      },
      size: {
        xs: 'ds-min-h-5.5 ds-px-2 ds-py-[3px] ds-gap-1.5 ds-text-xs ds-leading-4',
        sm: 'ds-min-h-8 ds-px-2.5 ds-py-1.5 ds-gap-1.5 ds-text-sm ds-leading-5',
        default: 'ds-min-h-9 ds-px-2.5 ds-py-2 ds-gap-2 ds-text-sm ds-leading-5',
        lg: 'ds-min-h-10 ds-px-2.5 ds-py-2.5 ds-gap-2 ds-text-base ds-leading-6',
        // shadcn's icon-only sizes.
        icon: 'ds-size-9',
        'icon-xs': "ds-size-6 ds-rounded-md [&_svg:not([class*='size-'])]:ds-size-3",
        'icon-sm': 'ds-size-8',
        'icon-lg': 'ds-size-10'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a <button>, keeping the styling. */
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', asChild = false, ...props }, ref) => {
    if (asChild) {
      return <Slot ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
    }
    return (
      <button
        ref={ref}
        type={type}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
